// react and js
import PropTypes from "prop-types";
import { React, useState, useEffect, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation, Trans } from "react-i18next";

// mui
import {
  Grid,
  Stack,
  Typography,
  Box,
  FormControl,
  Select,
  NativeSelect,
} from "@mui/material";

// components
import Iconify from "../../components/Iconify";
import backendRequest from "../../components/BackendRequest";
import CardOutlet from "../../components/CardOutlet";

// hooks
import appContext from "../../hooks/appContext";
import { stateStorageInit } from "../../hooks/stateStorageInit";

// layouts

// pages

// theme
import {
  B1Tiny,
  B3Small,
  CardOutlinedInput,
  CardMenuItem,
  AuthenticationAlertWarning,
} from "../../theme/styled";

// utils
import { TranslatedMessage } from "../../utils/common";

// i18n
import { localizeExternalUrl } from "../../i18n/languages";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function formatBytes(bytes, decimals = 2) {
  if (!+bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

function ValueLine({ parameter, value, widthLeft, widthRight }) {
  ValueLine.propTypes = {
    parameter: PropTypes.any.isRequired,
    value: PropTypes.any,
    widthLeft: PropTypes.any.isRequired,
    widthRight: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();

  let valueOutlet = (
    <Typography variant="contentSmall" align="right">
      {value}
    </Typography>
  );

  if (value !== null && value !== undefined) {
    if (value === "1") {
      valueOutlet = (
        <Iconify
          color="#61CE70"
          icon="eva:checkmark-outline"
          minWidth={22}
          minHeight={22}
          sx={{ my: "-5px" }}
        />
      );
    }

    if (value === "0") {
      valueOutlet = (
        <Iconify
          color="#C85439"
          icon="eva:close-outline"
          minWidth={22}
          minHeight={22}
          sx={{ my: "-5px" }}
        />
      );
    }

    if (value === "extended_client_data_missing") {
      valueOutlet = (
        <Typography variant="contentSmall" align="right">
          {t("downloads.common.missing")}
        </Typography>
      );
    }
  }

  return (
    <Box
      sx={{
        mb: "5px",
        p: "5px 0px 0px 0px",
        display: "flex",
        borderBottom: "1px solid #C8C8C8",
        borderColor: "#C8C8C8",
      }}
    >
      <Box
        sx={{
          width: widthLeft,
          pr: 1,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-end",
        }}
      >
        <Typography variant="contentSmall" display="block" align="left">
          {parameter}
        </Typography>
      </Box>

      <Box
        sx={{
          width: widthRight,
          pl: 1,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "flex-end",
        }}
      >
        <Typography
          variant="contentSmall"
          display="block"
          align="right"
          sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
        >
          {valueOutlet}
        </Typography>
      </Box>
    </Box>
  );
}

function Item({ header, subtitle, content }) {
  Item.propTypes = {
    header: PropTypes.any.isRequired,
    subtitle: PropTypes.any.isRequired,
    content: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);

  let currentYearAvailable = true;
  if (content[1].installer_end_date === "1970-01-31") {
    currentYearAvailable = false;
  }

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Box
        sx={{
          borderRadius: "10px",
          boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                  transition: "box-shadow 0.3s ease-in-out",
                  "&:hover": {
                    boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                  },

          background: "white",
          
        }}
      >
        <Stack>
          <Box
            sx={{ backgroundColor: "#C8C8C8", borderRadius: "10px 10px 0px 0px", }}
            display="block"
            align="center"
          >
            <Typography
              variant="h5"
              display="block"
              align="center"
              color="black"
              fontWeight="bold"
              sx={{ mt: "14px" }}
            >
              {header}
            </Typography>

            <Typography
              variant="contentMiddle"
              display="block"
              align="center"
              sx={{ mb: "14px" }}
            >
              {subtitle}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
            }}
          >
            <ValueLine
              parameter={t("downloads.tickData.item.fileSizeFullYears")}
              value={formatBytes(content[0].file_size)}
              widthLeft="60%"
              widthRight="40%"
            />

            {currentYearAvailable === true ? (
              <ValueLine
                parameter={t("downloads.tickData.item.fileSizeCurrentYear")}
                value={formatBytes(content[1].file_size)}
                widthLeft="60%"
                widthRight="40%"
              />
            ) : null}

            <ValueLine
              parameter={t("downloads.tickData.item.tickDataStart")}
              value={content[0].start_date}
              widthLeft="50%"
              widthRight="50%"
            />

            <Box sx={{ mt: 2 }} />

            <B1Tiny
              sx={{ mb: currentYearAvailable === true ? 1 : 0 }}
              fullWidth
              variant="contained"
              href={`${process.env.REACT_APP_URL_API_DOWNLOAD}/?tag=${content[0].tag}&token=${stateStorage.userInformation.answer.dashboard_access_token}`}
            >
              {t("downloads.tickData.item.downloadFullYears")}
            </B1Tiny>

            {currentYearAvailable === true ? (
              <B1Tiny
                fullWidth
                variant="contained"
                href={`${process.env.REACT_APP_URL_API_DOWNLOAD}/?tag=${content[1].tag}&token=${stateStorage.userInformation.answer.dashboard_access_token}`}
              >
                {t("downloads.tickData.item.downloadCurrentYear")}
              </B1Tiny>
            ) : null}
          </Box>
        </Stack>
      </Box>
    </Grid>
  );
}

function DownloadItem({ content, selection }) {
  DownloadItem.propTypes = {
    content: PropTypes.any.isRequired,
    selection: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();

  return (
    <>
      <Item
        header={t("downloads.tickData.spreadHeaders.low")}
        subtitle={selection}
        content={content.spread_low}
      />
      <Item
        header={t("downloads.tickData.spreadHeaders.medium")}
        subtitle={selection}
        content={content.spread_medium}
      />
      <Item
        header={t("downloads.tickData.spreadHeaders.high")}
        subtitle={selection}
        content={content.spread_high}
      />
    </>
  );
}

function WidgetMainContentProcessed({ content }) {
  WidgetMainContentProcessed.propTypes = {
    content: PropTypes.any.isRequired,
  };

  const { t, i18n } = useTranslation();
  // eslint-disable-next-line
  const { stateStorage } = useContext(appContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const checkoutUrl = localizeExternalUrl(
    `${process.env.REACT_APP_URL_FRONTEND_CHECKOUT}/distribution-tick-data`,
    i18n.language
  );

  let keys = "";
  let checkKey = "";
  let tickDataSubscriptionsAvailable = false;

  if (content === undefined || content === null) {
    checkKey = false;
  } else {
    keys = Object.keys(content);
    checkKey = keys.includes("tick_data");
  }

  keys = Object.keys(content.tick_data);
  const symbols = [];

  for (let index = 0; index < keys.length; index += 1) {
    symbols.push(keys[index]);
  }

  if (symbols.length === 0) {
    checkKey = true; // false;
  } else {
    tickDataSubscriptionsAvailable = true;
  }

  let downloadTickDataSymbolInitValue = 0;

  const myLocalSettingStorage = JSON.parse(
    localStorage.getItem("myLocalSettingStorage")
  );

  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs4935af76d5753ad6 !== null &&
      myLocalSettingStorage.obfs4935af76d5753ad6 !== undefined
    ) {
      if (Number(myLocalSettingStorage.obfs4935af76d5753ad6) < symbols.length) {
        downloadTickDataSymbolInitValue =
          myLocalSettingStorage.obfs4935af76d5753ad6;
      }
    }
  }

  const [selectedSymbol, setSelectedSymbol] = useState(
    downloadTickDataSymbolInitValue
  );

  if (selectedSymbol >= symbols.length && selectedSymbol !== symbols.length) {
    setSelectedSymbol(0);
  }

  return checkKey ? (
    <Grid container spacing={3} sx={{ p: 0 }}>
      <Grid item xs={12} md={12} lg={12}>
        <Box sx={{ mt: 0, mb: 0 }}>
          <CardOutlet
            variant={symbols.length > 1 ? "outlet" : "description"}
            backgroundProp="#F2F2F2"
            title={t("downloads.tickData.card.title")}
            subheader={
              <>
                {tickDataSubscriptionsAvailable
                  ? t("downloads.tickData.card.subtitleAvailable")
                  : t("downloads.tickData.card.subtitleUnavailable")}
                {tickDataSubscriptionsAvailable ? (
                  <AuthenticationAlertWarning
                    severity="warning"
                    sx={{
                      mt: 2,
                      "& .MuiAlert-message": { lineHeight: 1.6 },
                    }}
                  >
                    <Trans i18nKey="downloads.tickData.card.noteFullYears">
                      <strong>Note:</strong>{" "}
                      &quot;Full years&quot; includes complete past years (e.g.
                      2003-2022). &quot;Current year&quot; covers this year up to
                      today (e.g. 2023/01-2023/05). To get the full tick data
                      history, download and install both packages. For monthly
                      updates afterwards, you only need to redownload and
                      reinstall the current year package.
                    </Trans>
                  </AuthenticationAlertWarning>
                ) : null}
              </>
            }
            mainOutlet={
              isDesktop ? (
                <Box>
                  <Typography variant="contentSmallBold">{t("downloads.tickData.symbolLabel")}</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedSymbol}
                      onChange={(event) => {
                        setSelectedSymbol(event.target.value);

                        const newLocalSettingStorage = JSON.parse(
                          localStorage.getItem("myLocalSettingStorage")
                        );

                        newLocalSettingStorage.obfs4935af76d5753ad6 =
                          event.target.value;
                        localStorage.setItem(
                          "myLocalSettingStorage",
                          JSON.stringify(newLocalSettingStorage)
                        );
                      }}
                      input={<CardOutlinedInput />}
                    >
                      {symbols.map((symbol, index) => (
                        <CardMenuItem key={symbol} value={index}>
                          {symbol.replace("_", ".")}
                        </CardMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ) : (
                <Box>
                  <Typography variant="contentSmallBold">{t("downloads.tickData.symbolLabel")}</Typography>
                  <FormControl fullWidth>
                    <NativeSelect
                      value={selectedSymbol}
                      onChange={(event) => {
                        setSelectedSymbol(event.target.value);

                        const newLocalSettingStorage = JSON.parse(
                          localStorage.getItem("myLocalSettingStorage")
                        );

                        newLocalSettingStorage.obfs4935af76d5753ad6 =
                          event.target.value;
                        localStorage.setItem(
                          "myLocalSettingStorage",
                          JSON.stringify(newLocalSettingStorage)
                        );
                      }}
                      input={<CardOutlinedInput />}
                    >
                      {symbols.map((symbol, index) => (
                        <option key={symbol} value={index}>
                          {symbol.replace("_", ".")}
                        </option>
                      ))}
                    </NativeSelect>
                  </FormControl>
                </Box>
              )
            }
          />
        </Box>
      </Grid>

      {symbols[selectedSymbol] !== undefined ? (
        <DownloadItem
          content={content.tick_data[symbols[selectedSymbol]]}
          selection={symbols[selectedSymbol]}
        />
      ) : null}

      {!tickDataSubscriptionsAvailable ? (
        <Grid item xs={12} md={12} lg={12}>
          <Box
            display="block"
            align="center"
            sx={{
              borderRadius: "10px",
              py: 1,
              boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
              transition: "box-shadow 0.3s ease-in-out",
              backgroundColor: "white",
              "&:hover": {
                boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
              },
            }}
          >
            <Box sx={{ mx: 4, my: "14px" }}>
              <Typography
                variant="contentBig"
                display="block"
                align="center"
                sx={{ mb: 2 }}
              >
                {t("downloads.tickData.noSubscription")}
              </Typography>
              <B3Small
                fullWidth
                variant="outlined"
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("downloads.tickData.plansButton")}
              </B3Small>
            </Box>
          </Box>
        </Grid>
      ) : null}
    </Grid>
  ) : (
    <> </>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function Widget() {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  Widget.propTypes = {};

  const { stateStorage, setStateStorage } = useContext(appContext);

  const [error, setError] = useState(false);
  const [, setWarning] = useState(false);
  const [, setSuccess] = useState(false);

  const [loading, setLoading] = useState(navigator.onLine);
  const [content, setContent] = useState();
  const [, setReturnMessage] = useState("");

  const [regularRefreshIntervalCounter, setRegularRefreshIntervalCounter] =
    useState(0);

  // ######################################################
  // Main backend request
  // ######################################################
  function MainRequest(periodic) {
    if (
      localStorage.getItem("myReauthentication") === "false" &&
      navigator.onLine === true
    ) {
      if (periodic === false) {
        setStateStorage((previousState) => ({
          ...previousState,
          widgetUpdateDownloadsMt5TickData: false,
        }));

        setError(false);
        setWarning(false);
        setSuccess(false);
        setReturnMessage("");
        setLoading(true);
      }

      const sendData = {
        request_type: "dashboard",
        route_info: "downloads",
        operation: "get_essentials",
        client: {
          dashboard_access_token:
            stateStorage.userInformation.answer.dashboard_access_token,
        },
      };
      backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
        .then((contentFromBackend) => {
          const keyOnly = Object.keys(contentFromBackend);
          const checkKey = keyOnly.includes("message");

          if (checkKey === true) {
            if (contentFromBackend.message === "success") {
              setError(false);
              setLoading(false);
              setContent(contentFromBackend);
            } else if (contentFromBackend.message === "token invalid") {
              setWarning(true);
              setLoading(false);
              setReturnMessage(<TranslatedMessage tKey="downloads.status.loggedOut" />);

              setStateStorage(stateStorageInit);
              localStorage.setItem("forcedLoggedOut", true);

              localStorage.setItem("myLocalStateStorage", JSON.stringify(""));
              localStorage.removeItem("myLocalStateStorage");

              sessionStorage.setItem("myLocalLastRoute", JSON.stringify(""));
              sessionStorage.removeItem("myLocalLastRoute");

              localStorage.setItem("myLocalSettingStorage", JSON.stringify(""));
              localStorage.removeItem("myLocalSettingStorage");

              localStorage.setItem("myReauthentication", JSON.stringify(""));
              localStorage.removeItem("myReauthentication");
            } else {
              setError(true);
              setLoading(false);
              setReturnMessage(<TranslatedMessage tKey="downloads.status.invalidRequest" />);
            }
          } else {
            setError(true);
            setLoading(false);
            setReturnMessage(<TranslatedMessage tKey="downloads.status.unknownError" />);
          }
        })
        .catch(() => {
          if (navigator.onLine === true) {
            setError(true);
            setLoading(false);
            setReturnMessage(<TranslatedMessage tKey="downloads.status.unknownError" />);
          }
        });
    }
  }

  // ######################################################
  // Inital and triggered refreshs
  // ######################################################
  useEffect(() => {
    if (!stateStorage.widgetUpdateDownloadsMt5TickData) return;

    MainRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateStorage.widgetUpdateDownloadsMt5TickData]);

  // ######################################################
  // Regular refreshs
  // ######################################################
  useEffect(() => {
    const timerID = setTimeout(() => {
      setRegularRefreshIntervalCounter(regularRefreshIntervalCounter + 1);
    }, stateStorage.widgetUpdateIntervalDownloadsMt5TickData);

    return () => {
      clearTimeout(timerID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  useEffect(() => {
    MainRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  // ######################################################
  // Data representation
  // ######################################################
  let WidgetOutlet = null;
  if (loading) {
    WidgetOutlet = null;
  } else if (error) {
    WidgetOutlet = null;
  } else if (content) {
    WidgetOutlet = <WidgetMainContentProcessed content={content} />;
  }

  // ######################################################
  // Outlet
  // ######################################################
  return WidgetOutlet;
}
